import { useState } from "react";
import Select from "react-select";
import moment from "moment";
import {
  SearchIcon,
  SelectDropdownIndicator,
} from "../../modification-requests/components/Icons";

// Mirrors the booking list filter styling (search box + collapsible panel),
// adapted to the invoice list's single-select filters which auto-apply.
const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "45px",
    borderRadius: "5px",
    borderColor: "#D9D9D9",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
    "&:hover": { borderColor: "#D9D9D9" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  singleValue: (base) => ({ ...base, color: "#282828", fontSize: "15px", lineHeight: "22px" }),
  placeholder: (base) => ({ ...base, color: "#7C7B7B", fontSize: "15px", lineHeight: "22px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, paddingRight: "15px", color: "#282828" }),
  menu: (base) => ({ ...base, borderRadius: "5px", marginTop: "6px", zIndex: 30 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#F3F3F3" : "#FFFFFF",
    color: "#282828",
    cursor: "pointer",
    fontSize: "15px",
    lineHeight: "22px",
    "&:hover": { backgroundColor: "#F7F7F7" },
  }),
};

const paymentStatusOptions = [
  { value: "", label: "All statuses" },
  { value: "UNPAID", label: "Unpaid" },
  { value: "CHARGE_PENDING", label: "Charge Pending" },
  { value: "PAID", label: "Paid" },
];

const FieldLabel = ({ children }) => (
  <div className="tw:mb-2 tw:text-[14px] tw:leading-5 tw:text-[#7C7B7B]">{children}</div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Select
      value={value}
      onChange={(option) => onChange(option?.value ?? "")}
      options={options}
      placeholder="Select..."
      isSearchable={false}
      styles={selectStyles}
      components={{ DropdownIndicator: SelectDropdownIndicator, IndicatorSeparator: null }}
    />
  </div>
);

const InvoicesFilter = ({
  search,
  setSearch,
  month,
  setMonth,
  year,
  setYear,
  paymentStatus,
  setPaymentStatus,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const currentYear = new Date().getFullYear();

  const monthOptions = [
    { value: "", label: "All months" },
    ...moment
      .localeData("en")
      .months()
      .map((m, i) => ({ value: String(i + 1), label: m })),
  ];

  const yearOptions = [{ value: "", label: "All years" }];
  for (let y = currentYear + 1; y >= currentYear - 4; y--) {
    yearOptions.push({ value: String(y), label: String(y) });
  }

  const findOption = (opts, val) =>
    opts.find((o) => String(o.value) === String(val)) || opts[0];

  const onClearFilters = () => {
    setSearch("");
    setMonth("");
    setYear("");
    setPaymentStatus("");
  };

  return (
    <section className="tw:mt-[32px] tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <div className="tw:flex tw:h-[45px] tw:w-full tw:max-w-[329px] tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:bg-white tw:pl-[15px] tw:pr-[19px]">
          <SearchIcon />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search business name / email"
            className="tw:h-full tw:w-full tw:border-0 tw:bg-transparent tw:p-0 tw:text-[15px] tw:leading-[22px] tw:text-[#282828] tw:outline-none"
          />
        </div>

        <button
          type="button"
          className="tw:border-0 tw:bg-transparent tw:p-0"
          onClick={() => setShowFilters((prev) => !prev)}
        >
          <span className="tw:flex tw:h-[45px] tw:w-[108px] tw:items-center tw:justify-between tw:rounded-[5px] tw:bg-[#282828] tw:px-[17px] tw:text-white tw:font-hanken-grotesk">
            <span className="tw:text-[15px] tw:leading-[22px]">Filter</span>
            <span className="tw:font-['Noto_Serif_JP'] tw:text-[14px] tw:font-bold">{showFilters ? "-" : "+"}</span>
          </span>
        </button>
      </div>

      {showFilters ? (
        <div className="tw:rounded-[5px] tw:bg-[#F9F9F9] tw:p-5">
          <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-3">
            <SelectField
              label="Month"
              value={findOption(monthOptions, month)}
              onChange={setMonth}
              options={monthOptions}
            />

            <SelectField
              label="Year"
              value={findOption(yearOptions, year)}
              onChange={setYear}
              options={yearOptions}
            />

            <SelectField
              label="Payment Status"
              value={findOption(paymentStatusOptions, paymentStatus)}
              onChange={setPaymentStatus}
              options={paymentStatusOptions}
            />
          </div>

          <div className="tw:mt-5 tw:flex tw:items-center tw:gap-2">
            <button
              type="button"
              className="tw:border-0 tw:bg-transparent tw:p-0"
              onClick={onClearFilters}
            >
              <span className="tw:flex tw:h-[45px] tw:w-[120px] tw:items-center tw:justify-center tw:rounded-[5px] tw:border tw:border-[#282828] tw:bg-white tw:text-[15px] tw:leading-[22px] tw:text-[#282828] tw:font-hanken-grotesk">
                Clear
              </span>
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default InvoicesFilter;
